import React, { Fragment, useState, ReactElement, useMemo } from "react";
import { gql, useMutation } from "urql";
import { cloneDeep } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { Button } from "../../../../components/src/Button";
import { Alert, AlertVariant } from "../../../../components/src/Alert";

import {
    SubmitButton,
    TextField,
    SelectField,
    NullableSelectField,
    NumberField,
    SearchListField,
    Label
} from "../../../../components/src/form";
import {
    CurrencyEnum,
    Instrument,
    InstrumentProductTypeEnum,
    InstrumentCategory,
    CollectionNameEnum,
    InstrumentProductTypeEnumDescriptions,
    CicAreaEnum
} from "../../types.generated";
import { useAlertTimeOut } from "../../../../components/src/common/Utils";
import { emptyObjectId, recursivelyRemoveKey } from "../../../../common/src";
import { formikUrqlErrorFormater } from "../../../../components/src/common/formik-urql-error-helper";

import { aliasLabel, defaultAlias } from "../../components/alias";
import { CIC_TYPE_OPTIONS } from "./CashOrBalanceInstrumentForm";
import { TooltipLabel } from "../../../../components/src/TooltipLabel";

const UPDATE_PARTY_INSTRUMENT = gql`
    mutation updateInstrument($input: UpdateInstrumentInput!) {
        updateInstrument(input: $input) {
            _id
            clientIds
            cic
        }
    }
`;

interface IPartyInstrumentForm {
    partyId: string;
    instrument: Instrument;
    externalAccounts: {
        _id: string;
        name: string;
    }[];
    accounts: {
        _id: string;
        name: string;
    }[];
    parties: {
        _id: string;
        name: string;
    }[];

    onUpdate: any;
}

const currencyOptions: {
    key: string;
    value: string;
}[] = [
    { key: null, value: "None" },
    ...Object.keys(CurrencyEnum).map((currency) => {
        return { key: currency, value: currency };
    })
];

export const PartyInstrumentForm = ({
    partyId,
    instrument,
    externalAccounts,
    accounts,
    parties,
    onUpdate
}: IPartyInstrumentForm): ReactElement => {
    const [currentInstrument] = useState(cloneDeep(instrument));
    const [cic, setCic] = useState(currentInstrument.cic);
    const [_, updatePartyInstrument] = useMutation(UPDATE_PARTY_INSTRUMENT);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const aliasOptions: { _id: string; name: string }[] = useMemo(() => {
        let options = [{ _id: "000000000000000000000000", name: "None" }];
        if (parties) {
            options = [{ _id: "000000000000000000000000", name: CollectionNameEnum.None }, ...parties];
        }
        return options;
    }, [parties]);

    useAlertTimeOut(alert, setAlert, 5);

    return (
        <div id="partyinstrumentform" className="partyinstrumentform form">
            <Formik
                enableReinitialize={true}
                validateOnMount={true}
                initialValues={currentInstrument}
                validationSchema={Yup.object({
                    longName: Yup.string().required().min(2)
                })}
                validate={() => {
                    const errors: any = {};
                    return Object.keys(errors).length > 0 ? errors : {};
                }}
                onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                    const input: any = recursivelyRemoveKey(submitValues, "__typename");
                    delete input.cic;

                    await updatePartyInstrument({ input })
                        .then((result) => {
                            if ("error" in result && result.error) {
                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                setAlert({ color: "danger", visible: true, message });
                            } else {
                                setCic(result.data.updateInstrument.cic);
                                onUpdate(result.data.updateInstrument.clientIds);
                                setAlert({ color: "success", visible: true, message: `The instrument has been updated successfully!` });
                            }
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, values }) => {
                    return (
                        <Fragment>
                            <div className="flex flex-wrap -mx-2 mt-5">
                                <div className="w-full md:w-full px-2 mb-4">
                                    <h4>Party Instrument</h4>
                                    <Form autoComplete="off">
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-6/12 px-2 mb-4">
                                                <TextField name="name" label="Name" type="text" className="" disabled={isSubmitting} />
                                            </div>
                                            <div className="w-full md:w-6/12 px-2 mb-4">
                                                <TextField
                                                    name="longName"
                                                    label="Long name*"
                                                    type="text"
                                                    className=""
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-6/12 px-2 mb-4">
                                                <SelectField
                                                    name="accountId"
                                                    label={
                                                        <TooltipLabel
                                                            text={"Account"}
                                                            content={
                                                                "Account is used as default account when creating transactions. Primarily important for cash accounts."
                                                            }
                                                        />
                                                    }
                                                    options={[
                                                        ...[{ key: emptyObjectId, value: "None" }],
                                                        ...accounts.map((d) => ({ key: d._id, value: d.name }))
                                                    ]}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    size={12}
                                                />
                                            </div>
                                            <div className="w-full md:w-6/12 px-2 mb-4">
                                                <SelectField
                                                    name="category"
                                                    label="Category"
                                                    options={Object.keys(InstrumentCategory).sort()}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    size={12}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-6/12 px-2 mb-4">
                                                <SelectField
                                                    name="productType"
                                                    label="Product type"
                                                    options={Object.keys(InstrumentProductTypeEnum).sort()}
                                                    tooltips={InstrumentProductTypeEnumDescriptions}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    size={12}
                                                />
                                            </div>
                                            <div className="w-full md:w-6/12 px-2 mb-4">
                                                <SelectField
                                                    name="externalAccountId"
                                                    label="External account"
                                                    options={[
                                                        ...[{ key: emptyObjectId, value: "None" }],
                                                        ...externalAccounts.map((d) => ({ key: d._id, value: d.name }))
                                                    ]}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    size={12}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <TextField
                                                    name="bloombergTicker"
                                                    label="Bloomberg ticker"
                                                    type="text"
                                                    className=""
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <TextField name="bic" label="BIC" type="text" className="" disabled={isSubmitting} />
                                            </div>
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <NumberField
                                                    name="quantityDecimals"
                                                    label="Quantity decimals"
                                                    className=""
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-5/12 px-2 mb-4">
                                                <TextField name="iban" label="Iban" type="text" className="" disabled={isSubmitting} />
                                            </div>
                                            <div className="w-full md:w-3/12 px-2 mb-4">
                                                <NullableSelectField
                                                    name="currency"
                                                    label="Currency"
                                                    options={currencyOptions}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    size={12}
                                                />
                                            </div>
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <TextField name="isin" label="Isin" type="text" className="" disabled={isSubmitting} />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <SelectField
                                                    name="cicArea"
                                                    label="Cic area"
                                                    options={Object.keys(CicAreaEnum)}
                                                    className=""
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div className="w-full md:w-8/12 px-2 mb-4">
                                                <SearchListField
                                                    className=""
                                                    name="cicType"
                                                    label={"Cic type"}
                                                    options={CIC_TYPE_OPTIONS}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="mb-4 w-full px-2 mb-4">
                                                <Label name="cic" value="Cic (computed from Cic area and Cic type)" />
                                                <input id="cic" type="text" className="form-input" value={cic} disabled={true} />
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-5/12 px-2 mb-4">
                                                <h5>Aliases</h5>
                                                <FieldArray
                                                    name="aliases"
                                                    render={(arrayHelpers) => (
                                                        <Fragment>
                                                            {values.aliases && values.aliases.length > 0 ? (
                                                                values.aliases.map((alias, index) => (
                                                                    <div key={index} className="mb-4 w-full flex flex-wrap -mx-2">
                                                                        <div>
                                                                            <fieldset>
                                                                                <legend>{index + 1}</legend>
                                                                                <SelectField
                                                                                    name={`aliases[${index}].collection`}
                                                                                    label="Collection"
                                                                                    className="text-green-600"
                                                                                    options={["None", "Party"]}
                                                                                    disabled={isSubmitting}
                                                                                    size={5}
                                                                                />
                                                                                <SearchListField
                                                                                    className=""
                                                                                    name={`aliases[${index}].documentId`}
                                                                                    label={aliasLabel(alias.collection, alias.documentId)}
                                                                                    options={aliasOptions}
                                                                                    disabled={false}
                                                                                />
                                                                                <TextField
                                                                                    className=""
                                                                                    name={`aliases[${index}].key`}
                                                                                    label="Key"
                                                                                    disabled={false}
                                                                                />
                                                                                <TextField
                                                                                    className=""
                                                                                    name={`aliases[${index}].value`}
                                                                                    label="Value"
                                                                                    disabled={false}
                                                                                />
                                                                                <TextField
                                                                                    className=""
                                                                                    name={`aliases[${index}].comment`}
                                                                                    label="Comment"
                                                                                    disabled={false}
                                                                                />
                                                                            </fieldset>
                                                                        </div>
                                                                        <div className="w-full md:w-2/12 px-2 mb-4">
                                                                            <Button
                                                                                variant="danger"
                                                                                size="sm"
                                                                                className="mr-1 mt-2"
                                                                                type="button"
                                                                                onClick={() => arrayHelpers.remove(index)}
                                                                            >
                                                                                -
                                                                            </Button>
                                                                            <Button
                                                                                variant="primary"
                                                                                size="sm"
                                                                                className="mt-2"
                                                                                type="button"
                                                                                onClick={() => arrayHelpers.insert(index, defaultAlias)}
                                                                            >
                                                                                +
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() => arrayHelpers.push(defaultAlias)}
                                                                >
                                                                    +
                                                                </Button>
                                                            )}
                                                        </Fragment>
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {alert.visible ? (
                                            <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                                                {alert.message}
                                            </Alert>
                                        ) : null}

                                        <div className="inline-flex items-baseline">
                                            <SubmitButton disabled={isSubmitting} label={"Update"} />
                                            <a
                                                href={`/transactionitems?clientIds=${partyId}&instrument.name=${currentInstrument.name}`}
                                                className="ml-4"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Transactions
                                            </a>
                                        </div>
                                    </Form>
                                </div>
                            </div>
                        </Fragment>
                    );
                }}
            </Formik>
        </div>
    );
};
